import { FaApple, FaBell, FaChartLine, FaClipboardCheck, FaGooglePlay, FaLock, FaShieldAlt, FaUsers } from "react-icons/fa";
import PageWrapper from "../../components/ui/wrappers/PageWrapper";
import Seo from "../../components/Seo";
import H1Base from "../../components/ui/titles/H1Base";
import H2Base from "../../components/ui/titles/H2Base";
import PhoneMockup from "./PhoneMockup";
import StoreBadges from "./StoreBadges";
import DownloadCard from "./DownloadCard";
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "./storeLinks";
import dashboardScreen from "../../assets/app-screens/dashboard-screen.jpg";
import investScreen from "../../assets/app-screens/invest-screen.jpg";
import qrIos from "../../assets/app-screens/QR-Ios.PNG";
import qrAndroid from "../../assets/app-screens/QR-Android.PNG";

const features = [
    {
        Icon: FaChartLine,
        title: "Suivi en temps réel",
        description: "Consultez l'évolution de vos investissements et le remboursement des entreprises financées, à tout moment.",
    },
    {
        Icon: FaBell,
        title: "Alertes personnalisées",
        description: "Recevez une notification dès qu'un nouveau projet correspond à vos critères d'investissement.",
    },
    {
        Icon: FaShieldAlt,
        title: "Sécurité renforcée",
        description: "Vos données et vos opérations sont protégées par les mêmes standards de sécurité que la plateforme web.",
    },
];

const trustPoints = [
    {
        Icon: FaClipboardCheck,
        iconSize: 34,
        description: "Chaque entreprise financée fait l'objet d'une analyse approfondie avant d'être proposée aux investisseurs.",
    },
    {
        Icon: FaLock,
        iconSize: 32,
        description: "Vos fonds et vos données personnelles sont sécurisés selon les exigences applicables aux plateformes de financement participatif.",
    },
    {
        Icon: FaUsers,
        iconSize: 36,
        description: "Une équipe dédiée accompagne les investisseurs à chaque étape de leur projet.",
    },
];

const MobileApp = () => {
    return (
        <PageWrapper>
            <Seo
                title="Application mobile"
                description="Découvrez l'application mobile PretUp pour investir dans l'économie réelle, suivre vos financements et gérer votre portefeuille où que vous soyez."
            />
            <main>
                <section className="flex flex-col items-center gap-12 py-16 text-center md:flex-row md:gap-16 md:py-20 md:text-left">
                    <div className="flex-1">
                        <H1Base
                            text="L'investissement participatif, partout et en toute confiance"
                            className="mb-6 text-3xl md:text-4xl"
                        />
                        <p className="mb-8 text-lg text-gray-600 md:max-w-md">
                            Avec l'application PretUp, suivez vos financements aux entreprises, découvrez de
                            nouveaux projets et gérez votre portefeuille d'investisseur en toute sécurité, où que
                            vous soyez.
                        </p>
                        <StoreBadges className="justify-center md:justify-start" />
                    </div>
                    <div className="flex flex-1 justify-center">
                        <PhoneMockup src={dashboardScreen} alt="Tableau de bord de l'application PretUp" />
                    </div>
                </section>

                <section className="py-16 md:py-20">
                    <H2Base text="Une application pensée pour les investisseurs" tailwindClass="text-center text-2xl md:text-3xl" />
                    <div className="mt-10 grid grid-cols-1 gap-8 text-center md:grid-cols-3">
                        {features.map(({ Icon, title, description }) => (
                            <div key={title} className="px-4 py-6">
                                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondaryExtraLight text-secondary">
                                    <Icon size={28} />
                                </div>
                                <h3 className="mb-2 text-lg font-semibold text-secondary">{title}</h3>
                                <p className="text-gray-600">{description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="flex flex-col items-center gap-12 py-16 text-center md:flex-row md:gap-16 md:py-20 md:text-left">
                    <div className="flex-1">
                        <H2Base text="Investissez dans l'économie réelle en quelques secondes" tailwindClass="text-2xl md:text-3xl" />
                        <p className="text-gray-600">
                            Parcourez les projets de financement d'entreprises sélectionnés par nos équipes,
                            consultez leur dossier et investissez directement depuis votre mobile, en toute
                            transparence.
                        </p>
                    </div>
                    <div className="flex flex-1 justify-center">
                        <PhoneMockup src={investScreen} alt="Écran d'investissement de l'application PretUp" />
                    </div>
                </section>

                <section className="rounded-3xl bg-secondaryExtraLight px-6 py-16 md:py-20">
                    <H2Base text="Une plateforme de confiance" tailwindClass="text-center text-2xl md:text-3xl" />
                    <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {trustPoints.map(({ Icon, description, iconSize }, index) => (
                            <div key={index} className="flex items-center gap-3.5">
                                <Icon className="mt-0.5 flex-shrink-0 text-secondary" size={iconSize} />
                                <p className="text-sm text-gray-700">{description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="py-16 text-center md:py-20">
                    <H2Base text="Téléchargez l'application PretUp" tailwindClass="text-center text-2xl md:text-3xl" />
                    <p className="mx-auto mb-10 max-w-xl text-gray-600">
                        Scannez le code de votre store depuis votre ordinateur, ou touchez le bouton sur mobile.
                    </p>
                    <div className="mx-auto grid max-w-2xl grid-cols-1 gap-6 md:gap-16 md:grid-cols-2">
                        <DownloadCard
                            title="iPhone"
                            storeLabel="Télécharger sur"
                            storeName="App Store"
                            href={APP_STORE_URL}
                            ButtonIcon={FaApple}
                            iconSize={26}
                            qrSrc={qrIos}
                            TitleIcon={FaApple}
                            TitleIconSize={18}
                        />
                        <DownloadCard
                            title="Android"
                            TitleIcon={FaGooglePlay}
                            storeLabel="Disponible sur"
                            storeName="Google Play"
                            href={GOOGLE_PLAY_URL}
                            ButtonIcon={FaGooglePlay}
                            iconSize={24}
                            qrSrc={qrAndroid}
                        />
                    </div>
                </section>
            </main>
        </PageWrapper>
    );
};

export default MobileApp;
